import UUIDBase from '@/models/UUIDBase'
import { useI18nInstance } from '@/plugins/i18n'
import Dinero, { Currency, Dinero as DineroType } from 'dinero.js'
import { Transaction } from '../accounting/Transaction'
import Vendor from './Vendor'
import User from '../user/User'
import { Account } from '../accounting/Account'

const { t } = useI18nInstance().global

export const vendorBillI18nMessages = {
  en: {
    vendorName: 'Vendor Name',
    vendorBillID: 'Bill ID',
    dueDate: 'Due Date',
    billDate: 'Bill Date',
    receivedOn: 'Received On',
    createdAt: 'Created At',
    totalAmount: 'Total Amount',
    paidAmount: 'Paid Amount',
    balance: 'Balance',
    notes: 'Notes',
    vendorBillNumber: 'Vendor Bill Number',
    whatsFor: "What's For",
    debitedAccount: 'Debited Account',
    paidFrom: 'Paid from',
    attachments: 'Attachments'
  },
  ar: {
    vendorName: 'اسم المورّد',
    vendorBillID: 'رقم الطلب',
    dueDate: 'تاريخ الاستحقاق',
    billDate: 'تاريخ الفاتورة',
    receivedOn: 'تاريخ الاستلام',
    createdAt: 'تاريخ الاصدار',
    totalAmount: 'المبلغ الإجمالي',
    paidAmount: 'المبلغ المدفوع',
    balance: 'الرصيد',
    notes: 'الملاحظات',
    vendorBillNumber: 'رقم فاتورة المورد',
    whatsFor: 'الغرض من الفاتورة',
    debitedAccount: 'حساب المدين',
    paidFrom: 'حساب الدفع',
    attachments: 'المرفقات'
  }
}

export class VendorBillAttachment extends UUIDBase {
  static ENDPOINT = '/accounting/vendorbills/attachments'

  vendorBillID = ''
  filename = ''
  originalFilename = ''
  mimetype = ''

  static from(json: Record<string, unknown> | VendorBillAttachment) {
    const o = Object.assign(new VendorBillAttachment(), json)

    o.createdAt = new Date(json.createdAt as string)
    o.updatedAt = new Date(json.updatedAt as string)
    o.deletedAt = json.deletedAt ? new Date(json.deletedAt as string) : undefined

    return o
  }
}

export class VendorBillPayment extends UUIDBase {
  static ENDPOINT = '/accounting/vendorbills/payments'

  vendorBillID?: string
  vendorBill?: VendorBill
  transactionID?: string
  transaction?: Transaction

  totalDueAmount = 0
  totalDueDinero?: DineroType

  changeDueAmount = 0
  changeDueDinero?: DineroType

  paidBeforeChangeAmount = 0
  paidBeforeChangeDinero?: DineroType

  paymentAmount?: number
  paymentAmountDinero?: DineroType

  currency = ''
  precision = 0

  PayFromAccountID= ""

  static from(json: Record<string, unknown> | VendorBillPayment) {
    const o = Object.assign(new VendorBillPayment(), json)
    o.paymentAmountDinero = Dinero({
      amount: o.paymentAmount,
      currency: o.currency as Currency,
      precision: o.precision
    })

    o.totalDueDinero = Dinero({
      amount: o.totalDueAmount,
      currency: o.currency as Currency,
      precision: o.precision
    })

    o.changeDueDinero = Dinero({
      amount: o.changeDueAmount,
      currency: o.currency as Currency,
      precision: o.precision
    })

    o.paidBeforeChangeDinero = Dinero({
      amount: o.paidBeforeChangeAmount,
      currency: o.currency as Currency,
      precision: o.precision
    })
  }
}

export default class VendorBill extends UUIDBase {
  static from(json: Record<string, unknown> | VendorBill) {
    const o = Object.assign(new VendorBill(), json)
    o.user = User.from(o.user)

    o.totalDueDinero = Dinero({
      amount: o.totalDueAmount,
      currency: o.currency as Currency,
      precision: o.precision
    })

    o.totalPaidDinero = Dinero({
      amount: o.totalPaidAmount,
      currency: o.currency as Currency,
      precision: o.precision
    })

    o.totalBalanceDinero = Dinero({
      amount: o.totalBalanceAmount,
      currency: o.currency as Currency,
      precision: o.precision
    })

    o.otherCostsDinero = Dinero({
      amount: o.otherCostsAmount || 0,
      currency: o.currency as Currency,
      precision: o.precision
    })

    o.vendorBillPayments?.map((i) => {
      const x = VendorBillPayment.from(i)
      return x
    })

    if (o.transactions)
    o.transactions = o.transactions.map((transaction) => {
      return Transaction.from(transaction)
    })

    if (o.vendorBillAttachments)
    o.vendorBillAttachments = o.vendorBillAttachments.map((attachment) => {
      return VendorBillAttachment.from(attachment)
    })

    if (o.debitedAccount)
    o.debitedAccount = Account.from(o.debitedAccount)

    return o
  }
  static ENDPOINT = '/accounting/vendorbills'
  vendorName = ''
  vendorID?: string
  vendor?: Vendor
  externalID = '' // Bill #

  // Dates
  billDueDate?: Date
  billDate?: Date
  receivedOn?: Date

  userID?: string
  user = new User()

  // New fields
  vendorBillNumber = ''
  whatsFor = ''
  notes = ''

  // Debited Account
  debitedAccountID?: string
  debitedAccount?: Account

  totalDueAmount = 0
  totalDueDinero?: DineroType

  totalPaidAmount = 0
  totalPaidDinero?: DineroType

  totalBalanceAmount = 0
  totalBalanceDinero?: DineroType

  otherCostsAmount?: number
  otherCostsDinero?: DineroType

  currency?: string
  precision?: number

  vendorBillPayments?: VendorBillPayment[] //this should be a vendorBillPayment[]
  transactions = Array<Transaction>()
  vendorBillAttachments = Array<VendorBillAttachment>()

  referenceableID?: string
  referenceableType?: string
  requestOnlyPayFromAccountID= ""

  // For file uploads (not persisted, used in forms)
  attachmentFiles?: File[]
}
